import { useLocalStorage } from '@/hooks/useLocalStorage';

export interface StreamingPreferences {
  artistSatsPerMinute: number;
  appSatsPerMinute: number;
  enabled: boolean;
}

const DEFAULT_PREFERENCES: StreamingPreferences = {
  artistSatsPerMinute: 0, // Default to 0 - no payments
  appSatsPerMinute: 0,    // Default to 0 - no app support
  enabled: false,         // Disabled by default
};

// Helper to ensure values are valid numbers
function ensureNumber(value: unknown, defaultValue: number): number {
  if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
    return value;
  }
  return defaultValue;
}

// Normalize preferences to ensure all values are valid
function normalizePreferences(prefs: Partial<StreamingPreferences>): StreamingPreferences {
  return {
    artistSatsPerMinute: ensureNumber(prefs.artistSatsPerMinute, DEFAULT_PREFERENCES.artistSatsPerMinute),
    appSatsPerMinute: ensureNumber(prefs.appSatsPerMinute, DEFAULT_PREFERENCES.appSatsPerMinute),
    enabled: typeof prefs.enabled === 'boolean' ? prefs.enabled : DEFAULT_PREFERENCES.enabled,
  };
}

export function useStreamingPreferences() {
  const [rawPreferences, setPreferences] = useLocalStorage<StreamingPreferences>(
    'cove:streaming-preferences',
    DEFAULT_PREFERENCES
  );
  
  // Always normalize preferences to ensure valid values
  const preferences = normalizePreferences(rawPreferences);

  const updateArtistSats = (sats: number) => {
    setPreferences({ 
      ...preferences, 
      artistSatsPerMinute: Math.max(0, Math.min(1000, sats)),
      enabled: sats > 0 || preferences.appSatsPerMinute > 0,
    });
  };

  const updateAppSats = (sats: number) => {
    setPreferences({ 
      ...preferences, 
      appSatsPerMinute: Math.max(0, Math.min(100, sats)),
      enabled: preferences.artistSatsPerMinute > 0 || sats > 0,
    });
  };

  const toggleEnabled = () => {
    setPreferences({ ...preferences, enabled: !preferences.enabled });
  };

  const getArtistAmount = () => {
    return preferences.enabled ? preferences.artistSatsPerMinute : 0;
  };

  const getAppSupportAmount = () => {
    return preferences.enabled ? preferences.appSatsPerMinute : 0;
  };

  const getTotalPerMinute = () => {
    return getArtistAmount() + getAppSupportAmount();
  };

  // Legacy compatibility
  const satsPerMinute = preferences.artistSatsPerMinute;
  const updateSatsPerMinute = updateArtistSats;
  const supportApp = preferences.appSatsPerMinute > 0;
  const toggleAppSupport = () => {
    if (preferences.appSatsPerMinute > 0) {
      updateAppSats(0);
    } else {
      updateAppSats(5);
    }
  };

  return {
    preferences,
    updateArtistSats,
    updateAppSats,
    toggleEnabled,
    getArtistAmount,
    getAppSupportAmount,
    getTotalPerMinute,
    // Legacy
    satsPerMinute,
    updateSatsPerMinute,
    supportApp,
    toggleAppSupport,
  };
}
